<script setup>
// Interactive crop selector. Shows the ORIGINAL image (a dimmed "ghost") with a
// bright, draggable + resizable frame over it. The frame maps directly onto
// imgproxy's crop option:
//   frame size   -> crop width/height (in SOURCE pixels)
//   frame center -> focus-point gravity fp:x:y (relative 0..1)
// Because fp centers the crop window on that point and we keep the frame fully
// inside the image, what's inside the frame is exactly what imgproxy will cut.
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'

const props = defineProps({
  src: { type: String, required: true }, // ghost image URL (the fitted original)
  naturalWidth: { type: Number, required: true },
  naturalHeight: { type: Number, required: true },
  modelValue: { type: Object, default: () => ({}) }, // { cw, ch, cfx, cfy }
  aspect: { type: [Number, null], default: null }, // locked w/h ratio, or null = free
  maxWidth: { type: Number, default: 560 },
  maxHeight: { type: Number, default: 440 },
})
const emit = defineEmits(['update:modelValue'])

const MIN = 24 // smallest frame side, in display px
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi)

// Display size: fit the original into the max box, preserving aspect ratio.
const display = computed(() => {
  const ratio = props.naturalWidth / props.naturalHeight
  let w = props.maxWidth
  let h = w / ratio
  if (h > props.maxHeight) {
    h = props.maxHeight
    w = h * ratio
  }
  return { w: Math.round(w), h: Math.round(h) }
})
const scale = computed(() => display.value.w / props.naturalWidth)

// The frame, in display pixels.
const rect = reactive({ x: 0, y: 0, w: 0, h: 0 })

function modelToRect() {
  const d = display.value
  const s = scale.value
  const m = props.modelValue || {}
  if (m.cw && m.ch) {
    let w = clamp(m.cw * s, MIN, d.w)
    let h = clamp(m.ch * s, MIN, d.h)
    const cx = (m.cfx ?? 0.5) * d.w
    const cy = (m.cfy ?? 0.5) * d.h
    Object.assign(rect, {
      x: clamp(cx - w / 2, 0, d.w - w),
      y: clamp(cy - h / 2, 0, d.h - h),
      w,
      h,
    })
  } else {
    // No crop yet — a centered 60% frame.
    const w = d.w * 0.6
    const h = d.h * 0.6
    Object.assign(rect, { x: (d.w - w) / 2, y: (d.h - h) / 2, w, h })
  }
}

// Guard so our own emit doesn't bounce back through the modelValue watcher.
let internal = false
function emitModel() {
  const d = display.value
  const s = scale.value
  internal = true
  emit('update:modelValue', {
    cw: Math.round(rect.w / s),
    ch: Math.round(rect.h / s),
    cfx: +((rect.x + rect.w / 2) / d.w).toFixed(4),
    cfy: +((rect.y + rect.h / 2) / d.h).toFixed(4),
  })
}

watch(
  () => props.modelValue,
  () => {
    if (internal) {
      internal = false
      return
    }
    modelToRect()
  },
  { deep: true },
)
// Re-fit when the source image (asset) changes.
watch(display, modelToRect)

// Apply an aspect-ratio lock: reshape the frame around its center, keep in bounds.
watch(
  () => props.aspect,
  (a) => {
    if (!a) return
    const d = display.value
    const cx = rect.x + rect.w / 2
    const cy = rect.y + rect.h / 2
    let w = rect.w
    let h = w / a
    if (h > d.h) {
      h = d.h
      w = h * a
    }
    if (w > d.w) {
      w = d.w
      h = w / a
    }
    Object.assign(rect, {
      x: clamp(cx - w / 2, 0, d.w - w),
      y: clamp(cy - h / 2, 0, d.h - h),
      w,
      h,
    })
    emitModel()
  },
)

modelToRect()

// ---------------------------------------------------------------------------
// Pointer drag: move the frame or resize from a corner.
// ---------------------------------------------------------------------------
const containerRef = ref(null)
let mode = null // 'move' | 'nw' | 'ne' | 'sw' | 'se'
let start = null

function localPoint(e) {
  const r = containerRef.value.getBoundingClientRect()
  const d = display.value
  return {
    x: clamp(e.clientX - r.left, 0, d.w),
    y: clamp(e.clientY - r.top, 0, d.h),
  }
}

function onDown(e, m) {
  mode = m
  start = { p: localPoint(e), rect: { ...rect } }
  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
  e.preventDefault()
  e.stopPropagation()
}

function onMove(e) {
  if (!mode) return
  const d = display.value
  const p = localPoint(e)
  if (mode === 'move') {
    const dx = p.x - start.p.x
    const dy = p.y - start.p.y
    rect.x = clamp(start.rect.x + dx, 0, d.w - start.rect.w)
    rect.y = clamp(start.rect.y + dy, 0, d.h - start.rect.h)
  } else {
    resizeCorner(p, d)
  }
  emitModel()
}

function onUp() {
  mode = null
  window.removeEventListener('pointermove', onMove)
  window.removeEventListener('pointerup', onUp)
}

function resizeCorner(p, d) {
  const a = props.aspect
  const sr = start.rect
  const right = sr.x + sr.w
  const bottom = sr.y + sr.h
  const east = mode.includes('e')
  const west = mode.includes('w')
  const south = mode.includes('s')
  const north = mode.includes('n')

  let x = sr.x
  let y = sr.y
  let w = sr.w
  let h = sr.h

  if (east) w = clamp(p.x - sr.x, MIN, d.w - sr.x)
  if (west) {
    const nx = clamp(p.x, 0, right - MIN)
    x = nx
    w = right - nx
  }
  if (south) h = clamp(p.y - sr.y, MIN, d.h - sr.y)
  if (north) {
    const ny = clamp(p.y, 0, bottom - MIN)
    y = ny
    h = bottom - ny
  }

  if (a) {
    // Drive height from width, then keep the anchored corner fixed & in bounds.
    h = w / a
    if (south && sr.y + h > d.h) {
      h = d.h - sr.y
      w = h * a
    }
    if (north && bottom - h < 0) {
      h = bottom
      w = h * a
    }
    y = north ? bottom - h : sr.y
    x = west ? right - w : sr.x
  }

  w = clamp(w, MIN, d.w)
  h = clamp(h, MIN, d.h)
  x = clamp(x, 0, d.w - w)
  y = clamp(y, 0, d.h - h)
  Object.assign(rect, { x, y, w, h })
}

onBeforeUnmount(onUp)

const corners = [
  { k: 'nw', cls: '-left-1.5 -top-1.5 cursor-nwse-resize' },
  { k: 'ne', cls: '-right-1.5 -top-1.5 cursor-nesw-resize' },
  { k: 'sw', cls: '-bottom-1.5 -left-1.5 cursor-nesw-resize' },
  { k: 'se', cls: '-bottom-1.5 -right-1.5 cursor-nwse-resize' },
]

// Crop size in source pixels, for the on-frame readout.
const label = computed(() => `${Math.round(rect.w / scale.value)} × ${Math.round(rect.h / scale.value)}`)
</script>

<template>
  <div
    ref="containerRef"
    class="relative mx-auto touch-none select-none overflow-hidden rounded-lg"
    :style="{ width: display.w + 'px', height: display.h + 'px' }"
  >
    <img :src="src" alt="original" class="pointer-events-none absolute inset-0 h-full w-full" draggable="false" />

    <!-- The frame. Its huge box-shadow darkens everything OUTSIDE it (clipped to
         the container), leaving the crop area bright. -->
    <div
      class="absolute cursor-move ring-2 ring-white/90"
      :style="{
        left: rect.x + 'px',
        top: rect.y + 'px',
        width: rect.w + 'px',
        height: rect.h + 'px',
        boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
      }"
      @pointerdown="onDown($event, 'move')"
    >
      <!-- rule-of-thirds guides -->
      <div class="pointer-events-none absolute inset-0">
        <div class="absolute inset-y-0 left-1/3 w-px bg-white/30" />
        <div class="absolute inset-y-0 left-2/3 w-px bg-white/30" />
        <div class="absolute inset-x-0 top-1/3 h-px bg-white/30" />
        <div class="absolute inset-x-0 top-2/3 h-px bg-white/30" />
      </div>

      <span
        class="pointer-events-none absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 font-mono text-[10px] text-white"
      >
        {{ label }}
      </span>

      <span
        v-for="c in corners"
        :key="c.k"
        class="absolute size-3 rounded-sm border border-primary bg-white shadow"
        :class="c.cls"
        @pointerdown="onDown($event, c.k)"
      />
    </div>
  </div>
</template>
